import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  Briefcase,
  FileText,
  MapPin,
  Clock,
  ArrowLeft,
  Zap,
  Lightbulb,
} from 'lucide-react'
import { Button } from '../../components/ui/Button'
import useJobStore from '../../store/jobStore'
import useOrgStore from '../../store/orgStore'
import useAuthStore from '../../store/authStore'
import { useLocale } from '../../i18n/LocaleProvider'
import toast from 'react-hot-toast'

export function NewJob() {
  const navigate = useNavigate()
  const { createJob } = useJobStore()
  const { fetchUsage } = useOrgStore()
  const { user } = useAuthStore()
  const { t } = useLocale()
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState({})

  const [form, setForm] = useState({
    title: '',
    description: '',
    requirements: '',
    location: '',
    employmentType: 'full-time',
  })

  const canCreateJobs = user?.role === 'owner' || user?.isPlatformAdmin === true

  useEffect(() => {
    if (!canCreateJobs) {
      toast.error(t('authenticated.newJob.toasts.createForbidden'))
      navigate('/dashboard', { replace: true })
    }
  }, [canCreateJobs, navigate])

  function handleChange(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }))
    }
  }

  function validate() {
    const newErrors = {}
    if (!form.title.trim()) newErrors.title = t('authenticated.newJob.errors.titleRequired')
    if (!form.description.trim())
      newErrors.description = t('authenticated.newJob.errors.descriptionRequired')
    if (form.description.trim().length < 50)
      newErrors.description =
        t('authenticated.newJob.errors.descriptionDetail')
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  async function handleSubmit(e) {
    e.preventDefault()
    if (!canCreateJobs) return
    if (!validate()) return

    setSaving(true)
    const result = await createJob(form)

    if (result.success) {
      await fetchUsage()
      toast.success(t('authenticated.newJob.toasts.created'))
      navigate(`/dashboard/jobs/${result.job.jobId}`)
    } else {
      toast.error(result.error || t('authenticated.newJob.toasts.failed'))
      setSaving(false)
    }
  }

  const tips = [
    t('authenticated.newJob.tips.detailedDescription'),
    t('authenticated.newJob.tips.mustHave'),
    t('authenticated.newJob.tips.seniority'),
    t('authenticated.newJob.tips.tools'),
    t('authenticated.newJob.tips.responsibilities'),
    t('authenticated.newJob.tips.consistentContext'),
  ]
  const scoringItems = [
    t('authenticated.newJob.scoringItems.domain'),
    t('authenticated.newJob.scoringItems.mustHave'),
    t('authenticated.newJob.scoringItems.seniority'),
    t('authenticated.newJob.scoringItems.responsibility'),
    t('authenticated.newJob.scoringItems.tools'),
    t('authenticated.newJob.scoringItems.reviewPoints'),
    t('authenticated.newJob.scoringItems.explanation'),
  ]

  if (!canCreateJobs) return null

  return (
    <div className="max-w-3xl mx-auto animate-fade-in">
      {/* Back button */}
      <button
        onClick={() => navigate('/dashboard')}
        className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        {t('authenticated.newJob.backToJobs')}
      </button>

      <div className="mb-6">
        <h1 className="page-title">{t('authenticated.newJob.title')}</h1>
        <p className="page-subtitle">
          {t('authenticated.newJob.subtitle')}
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Form */}
        <div className="lg:col-span-2">
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Job title */}
            <div className="card p-5">
              <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Briefcase className="w-4 h-4 text-brand-600" />
                {t('authenticated.newJob.details')}
              </h2>

              <div className="space-y-4">
                <div className="form-group">
                  <label className="label">
                    {t('authenticated.newJob.jobTitle')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    className={`input ${errors.title ? 'border-red-300 focus:ring-red-500' : ''}`}
                    placeholder={t('authenticated.newJob.jobTitlePlaceholder')}
                    value={form.title}
                    onChange={(e) => handleChange('title', e.target.value)}
                  />
                  {errors.title && (
                    <p className="error-text">{errors.title}</p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="form-group">
                    <label className="label">{t('authenticated.newJob.location')}</label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        className="input pl-9"
                        placeholder={t('authenticated.newJob.locationPlaceholder')}
                        value={form.location}
                        onChange={(e) =>
                          handleChange('location', e.target.value)
                        }
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="label">{t('authenticated.newJob.employmentType')}</label>
                    <div className="relative">
                      <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <select
                        className="input pl-9"
                        value={form.employmentType}
                        onChange={(e) =>
                          handleChange('employmentType', e.target.value)
                        }
                      >
                        <option value="full-time">{t('authenticated.newJob.employmentTypes.fullTime')}</option>
                        <option value="part-time">{t('authenticated.newJob.employmentTypes.partTime')}</option>
                        <option value="contract">{t('authenticated.newJob.employmentTypes.contract')}</option>
                        <option value="freelance">{t('authenticated.newJob.employmentTypes.freelance')}</option>
                        <option value="internship">{t('authenticated.newJob.employmentTypes.internship')}</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Job description */}
            <div className="card p-5">
              <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <FileText className="w-4 h-4 text-brand-600" />
                {t('authenticated.newJob.jobDescription')}
                <span className="text-red-500">*</span>
              </h2>

              <div className="form-group">
                <textarea
                  className={`input resize-none ${
                    errors.description ? 'border-red-300 focus:ring-red-500' : ''
                  }`}
                  rows={8}
                  placeholder={t('authenticated.newJob.descriptionPlaceholder')}
                  value={form.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                />
                <div className="flex justify-between mt-1">
                  {errors.description ? (
                    <p className="error-text">{errors.description}</p>
                  ) : (
                    <span />
                  )}
                  <span
                    className={`text-xs ${
                      form.description.length < 50
                        ? 'text-red-400'
                        : 'text-gray-400'
                    }`}
                  >
                    {t('authenticated.newJob.characters', { count: form.description.length })}
                  </span>
                </div>
              </div>
            </div>

            {/* Requirements */}
            <div className="card p-5">
              <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Zap className="w-4 h-4 text-brand-600" />
                {t('authenticated.newJob.requirements')}
              </h2>

              <div className="form-group">
                <label className="label">
                  {t('authenticated.newJob.keyRequirements')}
                  <span className="text-gray-400 font-normal ml-1">
                    {t('authenticated.newJob.optionalContext')}
                  </span>
                </label>
                <textarea
                  className="input resize-none"
                  rows={5}
                  placeholder={t('authenticated.newJob.requirementsPlaceholder')}
                  value={form.requirements}
                  onChange={(e) => handleChange('requirements', e.target.value)}
                />
              </div>
            </div>

            {/* Submit */}
            <div className="flex gap-3">
              <Button
                type="submit"
                variant="primary"
                size="lg"
                loading={saving}
                fullWidth
                icon={<Zap className="w-5 h-5" />}
              >
                {t('authenticated.newJob.submit')}
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="lg"
                onClick={() => navigate('/dashboard')}
                disabled={saving}
              >
                {t('common.cancel')}
              </Button>
            </div>
          </form>
        </div>

        {/* Tips sidebar */}
        <div className="space-y-4">
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="card p-5"
          >
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 rounded-lg bg-yellow-100 flex items-center justify-center">
                <Lightbulb className="w-4 h-4 text-yellow-600" />
              </div>
              <h3 className="font-semibold text-gray-900 text-sm">
                {t('authenticated.newJob.tipsTitle')}
              </h3>
            </div>
            <ul className="space-y-2">
              {tips.map((tip, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-400 mt-1.5 shrink-0" />
                  {tip}
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-brand-50 rounded-2xl border border-brand-100 p-5"
          >
              <h3 className="font-semibold text-brand-900 text-sm mb-2">
              {t('authenticated.newJob.scoringTitle')}
            </h3>
            <div className="space-y-2">
              {scoringItems.map((item) => (
                <div key={item} className="rounded-lg bg-white/70 px-3 py-2 text-xs text-brand-800 border border-brand-100">
                  {item}
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  )
}
